/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';

import { DIV } from 'sdi/components/elements';
import { create, IMapOptions, multiSelectOptions } from 'sdi/map';
import { none, some } from 'fp-ts/lib/Option';
import geocoder, { selectOnMapButton } from 'sdi/components/geocoder';
import { EmbedProfile } from 'sdi/source';
import { mapRightToolsSimplified } from 'sdi/map/controls';

import {
    getMapInfo,
    getEmbedProfile,
    getLayerInfoList,
    getCurrentSelection,
    withProfileFancy,
    getCurrentBaseLayer,
} from '../queries/app';
import { getView, getInteractionMode, getInteraction } from '../queries/map';
import {
    viewEvents,
    scalelineEvents,
    dispatchView,
    setInteraction,
    startMark,
    endMark,
    trackerEvents,
    startTracker,
} from '../events/map';
import { selectFeatures } from '../events/app';
import { getDatasetMetadata } from '../queries/app';
import fancy from './fancy';
import actions from './actions';

const logger = debug('sdi:comp/map');

export const mapName = 'main-view';

const getInfo = () => getMapInfo().fold(null, m => m);

// const selectFeature = (lid: string, fid: string | number) => {
//     setCurrentFeatureById(lid, fid);
//     setLayout('map-and-feature');
// };

// const clearSelection = () => {
//     unsetCurrentFeature();
//     setLayout('map');
// };

const options: IMapOptions = {
    getBaseLayer: getCurrentBaseLayer,
    getView,
    element: null,
    getMapInfo: getInfo,
    getMapLayerInfo: getLayerInfoList,
    updateView: viewEvents.updateMapView,
    setScaleLine: scalelineEvents.setScaleLine,
};

let mapSetTarget: (t: HTMLElement | null) => void;
let mapUpdate: () => void;

// const getSelected = () => ({
//     featureId: getFeatureId(),
//     layerId: getCurrentLayer(),
// });

const attachMap = () => (element: HTMLElement | null) => {
    // logger(`attachMap ${typeof element}`);
    if (!mapUpdate) {
        const { update, setTarget, selectable, markable, trackable } = create(
            mapName,
            {
                ...options,
                element,
            },
        );
        mapSetTarget = setTarget;
        mapUpdate = update;

        selectable(
            multiSelectOptions({
                selectFeatures,
                getSelected: getCurrentSelection,
            }),
            getInteraction,
        );
        markable({ startMark, endMark }, getInteraction);
        trackable(
            {
                resetTrack: trackerEvents.resetTrack,
                setCenter: center =>
                    viewEvents.updateMapView({ dirty: 'geo', center }),
                updateTrack: trackerEvents.updateTrack,
            },
            getInteraction,
        );

        // highlightable(getSelected);
    }
    if (element) {
        mapSetTarget(element);
    } else {
        mapSetTarget(null);
    }
};

const renderGeocoder = () =>
    geocoder({
        termConfig: { setView: dispatchView, setInteraction },
        layerConfig: {
            getMapId: () => getMapInfo().map(m => m.id),
            getDatasetMetadata,
            onLabelClick: (layerId, _field, _searchQuery, featureIds) =>
                selectFeatures(
                    featureIds.map(featureId => ({ layerId, featureId })),
                ),
            buttons: [
                (layerId, _field, _searchQuery, featureIds) =>
                    selectOnMapButton(() =>
                        selectFeatures(
                            featureIds.map(featureId => ({
                                layerId,
                                featureId,
                            })),
                        ),
                    ),
            ],
        },
    });

const fromProfile = (profile: EmbedProfile) => {
    switch (profile) {
        case 'simple':
            return none;
        case 'fancy':
            return none;
        case 'full-screen':
            return some(DIV('tools', renderGeocoder()));
        case 'all-tools':
            return some(
                DIV(
                    'tools',
                    renderGeocoder(),
                    mapRightToolsSimplified(dispatchView, startTracker),
                ),
            );
    }
};

const render = () => {
    if (mapUpdate) {
        mapUpdate();
    }

    return DIV(
        `map-wrapper map-interaction-${getInteractionMode()}`,
        DIV({
            className: 'map',
            ref: attachMap(),
        }),
        actions(),
        withProfileFancy(fancy),
        fromProfile(getEmbedProfile()),
    );
};

export default render;

logger('loaded');
